/**
 * Button component with SwitchConnect brand variants.
 * Based on shadcn/ui Button with Radix Slot for composition.
 */

import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sc-border-focus focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary:
          "bg-sc-primary text-white hover:bg-sc-primary-hover rounded-btn",
        secondary:
          "border border-sc-primary text-sc-primary hover:bg-sc-primary-light rounded-btn",
        ghost:
          "text-sc-primary hover:bg-sc-primary-light rounded-btn",
        destructive:
          "bg-sc-error text-white hover:bg-red-700 rounded-btn",
        outline:
          "border border-sc-glass-border bg-sc-surface text-sc-text hover:bg-sc-surface-hover rounded-btn",
        glass:
          "border border-sc-glass-border bg-sc-glass backdrop-blur-[10px] text-sc-text-muted hover:bg-sc-surface-hover rounded-btn",
        link:
          "text-sc-primary underline-offset-4 hover:underline",
      },
      size: {
        sm: "h-9 px-4 text-body-sm",
        md: "h-11 px-6 py-2.5",
        lg: "h-[54px] px-10 py-3.5",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  readonly asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
